import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { useToast } from '@aslanpay/portal-common-lib';

import { RECONCILIATION_FINDINGS_QUERY_KEY } from 'app/hooks/useFetchReconciliationFindings';

import ReconciliationsApiHandler from 'app/services/reconciliationsApiHandler';

import {
  ReconciliationFinding,
  ResolveReconciliationFindingRequest,
} from 'app/types/reconciliations';

const DEFAULT_ERROR_DESCRIPTION =
  'Sorry, the problem could not be marked as fixed. Please try again.';

const getErrorDescription = (error: Error) =>
  (axios.isAxiosError<{ message?: string }>(error) && error.response?.data?.message) ||
  DEFAULT_ERROR_DESCRIPTION;

/** Marks an open or past problem as fixed by hand, with a note saying why. */
const useResolveReconciliationFinding = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation<ReconciliationFinding, Error, ResolveReconciliationFindingRequest>(
    (request) => ReconciliationsApiHandler.resolveFinding(request),
    {
      onSuccess: () => {
        toast({ position: 'top-right', status: 'success', title: 'Problem marked as fixed' });
      },
      onError: (error) => {
        toast({
          position: 'top-right',
          status: 'error',
          title: 'Something went wrong',
          description: getErrorDescription(error),
        });
      },
      onSettled: () =>
        queryClient.invalidateQueries({ queryKey: [RECONCILIATION_FINDINGS_QUERY_KEY] }),
    }
  );
};

export default useResolveReconciliationFinding;
